import {
	GameConfigService,
	INotificationsService,
	IVehicleService,
} from '~/rage';
import { EventBus } from '~/rage/common';
import { notifyOnError } from '~/rage/decorators';
import {
	Gender,
	IDeathScreenSession,
	IAuthPayload,
	ICharacterInitialClientData,
	IPlayerJailInfo,
	IPlayerLevel,
	IPlayerSettings,
	ModalButtonColor,
	ModalType,
	numWord,
} from '~/shared';
import { IObservable, Subject } from '../../utils';
import { BackendService } from '../BackendService';
import { IModalService } from '../ModalService';
import { VarsService } from '../VarsService/VarsService';
import { IPriceConfig, IPlayerService } from './playerServiceTypes';

export class PlayerService implements IPlayerService {
	private readonly DEFAULT_PLAYER_SETTINGS: IPlayerSettings = {
		hudHelpButtonsVisible: true,
		techTuningDragHintShowed: false,
	};
	private confirmedPlayerSettings: IPlayerSettings = {
		...this.DEFAULT_PLAYER_SETTINGS,
	};
	private playerSettingsUpdateVersions: Record<keyof IPlayerSettings, number> =
		{
			hudHelpButtonsVisible: 0,
			techTuningDragHintShowed: 0,
		};
	private playerSettingsUpdateQueue: Promise<void> = Promise.resolve();

	private playerAccountId = new Subject<string | null>(null);
	private playerCharacterId = new Subject<string | null>(null);

	private playerLevel = new Subject<IPlayerLevel>({
		exp: 0,
		nextLevelExp: 0,
		level: 0,
		levelProgress: 0,
	});
	private earnedExp = new Subject<number | null>(null);
	private exp = new Subject<number>(0);
	private nextLevelExp = new Subject<number>(0);
	private lastEarnedExp = new Subject<number | null>(null);
	private registeredAt = new Subject<number>(0);
	private parkingSlotsPrice = new Subject<number>(0);
	private usedParkingSlots = new Subject<number>(0);
	private totalParkingSlots = new Subject<number>(0);
	private purchasedParkingSlots = new Subject<number>(0);
	private changeNamePrice = new Subject<number>(0);
	private parkingSlotPriceConfig = new Subject<IPriceConfig>({
		price: 0,
		discount: null,
		discountTimestampStart: null,
		discountTimestampEnd: null,
	});
	private changeNamePriceConfig = new Subject<IPriceConfig>({
		price: 0,
		discount: null,
		discountTimestampStart: null,
		discountTimestampEnd: null,
	});
	private playerJailInfo = new Subject<IPlayerJailInfo | null>(null);
	private hudHelpButtonsVisible = new Subject<boolean>(
		this.DEFAULT_PLAYER_SETTINGS.hudHelpButtonsVisible,
	);
	private techTuningDragHintShowed = new Subject<boolean>(
		this.DEFAULT_PLAYER_SETTINGS.techTuningDragHintShowed,
	);
	private maskedEmail = new Subject<string>('');

	private maxExpByLevel: Map<number, number> = new Map();

	private accountLogInPromise: Promise<void> | null = null;
	private accountLogInResolve: (() => void) | null = null;

	private characterSelectPromise: Promise<void> | null = null;
	private characterSelectResolve: (() => void) | null = null;

	constructor(
		private readonly vars: VarsService,
		private readonly backend: BackendService,
		private readonly modal: IModalService,
		private readonly notifications: INotificationsService,
		private readonly gameConfig: GameConfigService,
		vehicles: IVehicleService,
	) {
		EventBus.add(
			'backendTokenUpdated',
			this.handleBackendTokenUpdated.bind(this),
		);

		this.accountLogInPromise = new Promise((resolve) => {
			this.accountLogInResolve = resolve;
		});

		this.characterSelectPromise = new Promise((resolve) => {
			this.characterSelectResolve = resolve;
		});

		backend.addEventHandler('playerAccountLogIn', (accountId: string) => {
			this.playerAccountId.next(accountId);

			this.accountLogInResolve?.();
			this.accountLogInResolve = null;
			this.accountLogInPromise = null;
		});

		backend.addEventHandler('playerCharacterSelect', (characterId: string) => {
			this.playerCharacterId.next(characterId);

			this.characterSelectResolve?.();
			this.characterSelectResolve = null;
			this.characterSelectPromise = null;
		});

		this.gameConfig.getConfigs().subscribe((config) => {
			if (!config) {
				return;
			}

			if (config.gameplay) {
				config.gameplay.levels.forEach((levelConfig) => {
					this.maxExpByLevel.set(levelConfig.level, levelConfig.expRequired);
				});
				this.changeNamePrice.next(config.gameplay.changeNamePrice);
				this.changeNamePriceConfig.next({
					price: config.gameplay.changeNamePrice,
					discount: config.gameplay.changeNameDiscount,
					discountTimestampStart:
						config.gameplay.changeNameDiscountTimestampStart,
					discountTimestampEnd: config.gameplay.changeNameDiscountTimestampEnd,
				});
			}

			if (config.vehicles) {
				this.parkingSlotsPrice.next(config.vehicles.parkingSlotPrice);
				this.parkingSlotPriceConfig.next({
					price: config.vehicles.parkingSlotPrice,
					discount: config.vehicles.parkingSlotDiscount,
					discountTimestampStart:
						config.vehicles.parkingSlotDiscountTimestampStart,
					discountTimestampEnd: config.vehicles.parkingSlotDiscountTimestampEnd,
				});
			}
		});

		this.backend.addEventHandler(
			'level:update',
			(data: { level: number; exp: number; earnedExp?: number }) => {
				const oldLevel = this.playerLevel.getValue();
				const nextLevelExp = this.calculateNextLevelExp(data.level);

				const newLevel = {
					...oldLevel,
					level: data.level,
					exp: data.exp,
					nextLevelExp,
					levelProgress: data.exp / nextLevelExp,
				};

				this.playerLevel.next(newLevel);

				let expDiff = 0;

				if (data.level === oldLevel.level) {
					expDiff = data.exp - oldLevel.exp;
				} else if (data.level > oldLevel.level) {
					expDiff = oldLevel.nextLevelExp - oldLevel.exp + data.exp;
				}

				if (expDiff > 0) {
					setTimeout(() => {
						this.lastEarnedExp.next(expDiff);
					}, 50);
				}

				if (data.earnedExp !== undefined) {
					this.earnedExp.next(data.earnedExp);
				}
			},
		);

		vehicles.getPlayerVehicleList().subscribe((ownedVehicles) => {
			this.usedParkingSlots.next(ownedVehicles.length);
		});

		backend.addEventHandler(
			'character:init',
			this.handleCharacterInit.bind(this),
		);

		this.vars.get('playerJailInfo', null).subscribe((jailInfo) => {
			this.playerJailInfo.next(jailInfo);
		});
	}

	@notifyOnError
	private async handleBackendTokenUpdated(
		payload: IAuthPayload,
	): Promise<void> {
		const oldAccountId = this.playerAccountId.getValue();
		if (!oldAccountId && payload.accountId) {
			await this.backend.invoke('auth:playerAccountLogIn');
			return;
		}

		const oldCharacterId = this.playerCharacterId.getValue();
		if (!oldCharacterId && payload.characterId) {
			await this.backend.invoke('auth:playerCharacterSelect');
			return;
		}
	}

	private handleCharacterInit(payload: ICharacterInitialClientData) {
		this.registeredAt.next(payload.registeredAt);

		const settings: IPlayerSettings = {
			...this.DEFAULT_PLAYER_SETTINGS,
			...payload.playerSettings,
		};
		this.confirmedPlayerSettings = settings;
		this.hudHelpButtonsVisible.next(settings.hudHelpButtonsVisible);
		this.techTuningDragHintShowed.next(settings.techTuningDragHintShowed);
		this.maskedEmail.next(payload.maskedEmail ?? '');
	}

	public getNetId(): IObservable<number | null> {
		return this.vars.get('playerNetId', null);
	}

	public getAccountId(): IObservable<string | null> {
		return this.playerAccountId;
	}

	public getCharacterId(): IObservable<string | null> {
		return this.playerCharacterId;
	}

	// @TODO премиум
	public getPremium(): IObservable<boolean> {
		return this.vars.get('playerPremium', false);
	}

	// @TODO премиум
	public getPremiumDays(): IObservable<number> {
		return this.vars.get('playerPremiumDays', 0);
	}

	public getPlayerRegistrationDate(): IObservable<number> {
		return this.registeredAt;
	}

	public getNickname(): IObservable<string> {
		return this.vars.get('nickname', '');
	}

	public getUsedParkingSlots(): IObservable<number> {
		return this.usedParkingSlots;
	}

	public getTotalParkingSlots(): IObservable<number> {
		this.backend
			.invoke<{ total: number; purchased: number }>(
				'vehicleParkingSlots:getSlots',
			)
			.then((response) => {
				this.totalParkingSlots.next(response.total);
				this.purchasedParkingSlots.next(response.purchased);
			});

		return this.totalParkingSlots;
	}

	public async waitForAccountLogIn(): Promise<void> {
		await this.accountLogInPromise;
	}

	public async waitForCharacterSelect(): Promise<void> {
		await this.characterSelectPromise;
	}

	@notifyOnError
	public async buyParkingSlots(count: number): Promise<void> {
		const slots = await this.backend.invoke<{
			total: number;
			purchased: number;
		}>('vehicleParkingSlots:buySlots', {
			count,
		});

		this.totalParkingSlots.next(slots.total);
		this.purchasedParkingSlots.next(slots.purchased);

		const word = numWord(count, [
			'гаражное место',
			'гаражного места',
			'гаражных мест',
		]);
		this.notifications.showSuccess(`Вы успешно приобрели ${count} ${word}`);
	}

	public showBuyParkingSlotsModal(): void {
		const config = this.gameConfig.configs?.vehicles;
		if (!config) return;

		const currentPurchasedSlots = this.purchasedParkingSlots.getValue();

		this.modal.showModal(
			{
				type: ModalType.CountAndPrice,
				title: 'Гаражные места',
				text: '',
				acceptButton: { text: 'Оплатить', color: ModalButtonColor.Success },
				cancelButton: { text: 'Отменить', color: ModalButtonColor.Secondary },
				count: 1,
				maxCount: config.parkingSlotBuyMax - currentPurchasedSlots,
				minCount: 1,
				pricePerItem: this.getPriceWithDiscount(
					this.parkingSlotPriceConfig.getValue(),
				),
				wallet: config.parkingSlotPriceWallet,
			},
			async (result) => {
				if (!result.isAccepted) return;

				await this.buyParkingSlots(result.value);
			},
		);
	}

	public getDeathScreenSession(): IObservable<IDeathScreenSession | null> {
		return this.vars.get('deathScreen:session', null);
	}

	public getPlayerLevel(): IObservable<IPlayerLevel> {
		return this.playerLevel;
	}

	public getLastEarnedExp(): IObservable<number | null> {
		return this.vars.get('playerEarnedExp', null);
	}

	public clearLastEarnedExp(): void {
		this.lastEarnedExp.next(null);
	}

	private calculateNextLevelExp(level: number): number {
		return this.maxExpByLevel.get(level + 1) ?? 0;
	}

	private hasDiscountActive(
		priceConfig: IPriceConfig,
		now = Date.now(),
	): boolean {
		if (!priceConfig.discount) {
			return false;
		}

		if (
			priceConfig.discountTimestampStart === null ||
			priceConfig.discountTimestampEnd === null
		) {
			return false;
		}

		return (
			now >= priceConfig.discountTimestampStart &&
			now <= priceConfig.discountTimestampEnd
		);
	}

	private getPriceWithDiscount(
		priceConfig: IPriceConfig,
		now = Date.now(),
	): number {
		if (!this.hasDiscountActive(priceConfig, now)) {
			return priceConfig.price;
		}

		return Math.floor(priceConfig.price * (1 - (priceConfig.discount ?? 0)));
	}

	public getExp(): IObservable<number> {
		return this.exp;
	}

	public getNextLevelExp(): IObservable<number> {
		return this.nextLevelExp;
	}

	public getPlayerIsTalking(): IObservable<boolean> {
		return this.vars.get('localPlayerTalking', false);
	}

	public getPlayerGender(): IObservable<Gender> {
		return this.vars.get('playerGender', Gender.MALE);
	}

	public getParkingSlotsPrice(): IObservable<number> {
		return this.parkingSlotsPrice;
	}

	public getChangeNamePrice(): IObservable<number> {
		return this.changeNamePrice;
	}

	public getParkingSlotPriceConfig(): IObservable<IPriceConfig> {
		return this.parkingSlotPriceConfig;
	}

	public getChangeNamePriceConfig(): IObservable<IPriceConfig> {
		return this.changeNamePriceConfig;
	}

	public getPlayerJailInfo(): IObservable<IPlayerJailInfo | null> {
		return this.playerJailInfo;
	}

	public getHudHelpButtonsVisible(): IObservable<boolean> {
		return this.hudHelpButtonsVisible;
	}

	public async setHudHelpButtonsVisible(value: boolean): Promise<void> {
		await this.updatePlayerSettings({ hudHelpButtonsVisible: value });
	}

	public getTechTuningDragHintShowed(): IObservable<boolean> {
		return this.techTuningDragHintShowed;
	}

	public async setTechTuningDragHintShowed(value: boolean): Promise<void> {
		await this.updatePlayerSettings({ techTuningDragHintShowed: value });
	}

	@notifyOnError
	private async updatePlayerSettings(
		patch: Partial<IPlayerSettings>,
	): Promise<void> {
		let hudHelpButtonsVisibleVersion: number | null = null;
		if (patch.hudHelpButtonsVisible !== undefined) {
			hudHelpButtonsVisibleVersion = ++this.playerSettingsUpdateVersions
				.hudHelpButtonsVisible;
			this.hudHelpButtonsVisible.next(patch.hudHelpButtonsVisible);
		}

		let techTuningDragHintShowedVersion: number | null = null;
		if (patch.techTuningDragHintShowed !== undefined) {
			techTuningDragHintShowedVersion = ++this.playerSettingsUpdateVersions
				.techTuningDragHintShowed;
			this.techTuningDragHintShowed.next(patch.techTuningDragHintShowed);
		}

		const request = this.playerSettingsUpdateQueue.then(async () => {
			await this.backend.invoke('characters:updatePlayerSettings', patch);
		});
		this.playerSettingsUpdateQueue = request.catch(() => undefined);

		try {
			await request;
			this.confirmedPlayerSettings = {
				...this.confirmedPlayerSettings,
				...patch,
			};
		} catch (error) {
			if (
				hudHelpButtonsVisibleVersion !== null &&
				hudHelpButtonsVisibleVersion ===
					this.playerSettingsUpdateVersions.hudHelpButtonsVisible
			) {
				this.hudHelpButtonsVisible.next(
					this.confirmedPlayerSettings.hudHelpButtonsVisible,
				);
			}

			if (
				techTuningDragHintShowedVersion !== null &&
				techTuningDragHintShowedVersion ===
					this.playerSettingsUpdateVersions.techTuningDragHintShowed
			) {
				this.techTuningDragHintShowed.next(
					this.confirmedPlayerSettings.techTuningDragHintShowed,
				);
			}

			throw error;
		}
	}

	public getMaskedEmail(): IObservable<string> {
		return this.maskedEmail;
	}
}
